/*
 * Copyright (C) Serfim TIC
 * All rights reserved
 */
import { Filter } from '../../../model/Filter';
import { Command } from '../../../model/Command';
import { ListController } from '../../ListController';
import { DomainDeleteViewController } from './DomainDeleteView';
import { DomainViewController } from './DomainView';
import { SiteViewController } from './SiteView';

class SiteListController extends ListController {
    /** @ngInject */
    constructor(domainService, siteService, waitSpinnerService, $q, $state, $stateParams, $log, notificationService,
        configuration, filterService, dialogService, $scope) {
        super(configuration, notificationService, waitSpinnerService, $q, $state, $stateParams, $log);
        this.domainService = domainService;
        this.siteService = siteService;
        this.filterService = filterService;
        this.dialogService = dialogService;
        this.$scope = $scope;
        // mdDataTable doesn't support ES6 syntax with $ctrl variable in templates. That's why we must attach the reload
        // callback to the scope with an arrow function so as it can be called by this component with the correct 'this'
        // binding.
        this.$scope.reload = () => this.reload();
        this.$log.debug('Controller \'SiteList\' loaded');
    }

    $onInit() {
        super.$onInit();

        this.paging.sort = 'domain.name';
        this.commands = [];
        this.initFilters();

        this.handleRestServiceCall(this.loadFilters(), () => {
            this.initCommands();
            this.reload();
        }).finally(() => {
            this.waitSpinnerService.visible = false;
        });
    }

    /**
     * Initializes the filters available at the top of the view.
     */
    initFilters() {
        this.domainFilter = Filter.equalFilterEmpty('domain.id', 'model.domain.domain', 'SELECT', []);

        this.filters.push(this.domainFilter, Filter.emptyLikeFilter('name', 'model.site.name'));
    }

    /**
     * Initializes the commands available in the view header.
     */
    initCommands() {
        this.commands.push(
            Command.handlerCommand('ui.domain.create', 'images/svg/add.svg',
                () => this.showDomainViewDialog(false)),
            Command.handlerCommand('ui.domain.delete', 'images/svg/delete.svg',
                () => this.showDomainDeleteDialog()),
            Command.stateCommand('ui.site.create', 'images/svg/add.svg', 'gmao.organization.site.create'),
            Command.handlerCommand(undefined, 'images/svg/reset.svg', () => this.reload())
        );
    }

    /**
     * Loads data in filters.
     */
    loadFilters() {
        return this.getFilterDomains();
    }

    /**
     * Gets the list of domains.
     *
     * @return {Object[]} Promise providing an array of domains.
     */
    getFilterDomains() {
        return this.filterService.getFilterOptions(this.domainService, this.domainFilter, 'name');
    }

    /**
     * Reloads the list of entities.
     *
     * @return {Promise} A promise providing the list of entities.
     */
    reload() {
        return this.handleRestServiceCall(
            this.siteService.searchEntities({
                params: this.filterService.buildPagingParams(this.paging),
                data: this.filterService.buildFilters(this.filters)
            }),
            data => {
                this.model.data = data.content;
                this.model.totalElements = data.totalElements;
            }
        );
    }

    /**
     * Shows a dialog containing the form to create/update a domain. When the dialog is closed, the list of domains is
     * reloaded.
     *
     * @param {Object} [domain] Domain to be updated.
     */
    showDomainViewDialog(domainId, domainVersion, domainName) {
        this.dialogService.showDialog(DomainViewController, 'organization/component/domain-view.html', {
            domain: {
                id: domainId,
                version: domainVersion,
                name: domainName
            }
        }).then(() => this.reload());
    }

    /**
     * Shows a dialog containing a form to delete a domain. When the dialog is closed, the list of domains is
     * reloaded.
     */
    showDomainDeleteDialog() {
        this.dialogService.showDialog(DomainDeleteViewController, 'organization/component/domain-delete-view.html'
        ).then(
            () => this.reload()
        );
    }

    /**
     * Shows a confirmation dialog before deleting a site. If the dialog is confirmed, the site is deleted and the list
     * of domains is reloaded.
     *
     * @param {integer} id Site ID.
     * @return {Promise} A promise containing the confirmation result.
     */
    showSiteDeleteConfirmationDialog(id) {
        return this.showDeleteConfirmationDialog('ui.site.deleteconfirmation').then(confirmed => {
            if (confirmed) {
                this.deleteSite(id).then(() => {
                    this.reload();
                })
            }
            return confirmed;
        });
    }

    /**
     * Shows a confirmation dialog before deleting an entity. If the dialog is confirmed, the entity is deleted.
     *
     * @param {String} messageKey Message key displayed in the dialog (translated).
     * @return {Promise} A promise containing the confirmation result.
     */
    showDeleteConfirmationDialog(messageKey) {
        return this.dialogService.showConfirmationDialogWithMessage(messageKey);
    }

    /**
     * Deletes immediately a site without confirmation.
     *
     * @param {integer} id Domain ID.
     * @return {Promise} A promise which resolves when the delete is done.
     */
    deleteSite(id) {
        return this.handleRestServiceCall(this.siteService.deleteEntity(id), () => {
            this.notificationService.pushMessage('ui.site.deleted', this.configuration.NOTIFIER_LEVEL_INFO);
        });
    }
}

export const siteListComponent = {
    template: require('./site-list.html'),
    controller: SiteListController
};



// WEBPACK FOOTER //
// ./src/main/front/scripts/module/organization/component/SiteList.js